import { Dispatch, SetStateAction, useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { OutSourceConfig, Outsource, OutsourceCost, OutsourceProvider } from 'entities';

import { Column, LabeledTextarea } from 'components/Blocks';
import { OutsourceProviderSelect } from 'components/Entities';

import { MonetaryInput } from 'components/Inputs';

type OutsourceKey = keyof Outsource;

interface OutsourcingFormProps {
  outsource: OutSourceConfig | null;
  setOutsource: Dispatch<SetStateAction<OutSourceConfig>>;
}

export function OutsourcingForm({ outsource, setOutsource }: OutsourcingFormProps) {
  const { t } = useLang();

  const handleProviderChange = useCallback(
    (provider: OutsourceProvider) => {
      return setOutsource((old) => ({
        ...(old as OutSourceConfig),
        provider: provider.id,
        providerName: provider.name,
      }));
    },
    [setOutsource],
  );

  const handleChange = useCallback(
    ({ target }: { target: { name: string; value: string } }) => {
      const { name, value } = target;

      const [firstLevelProp, secondLevelProp] = name.split('#');

      if (!secondLevelProp)
        return setOutsource((old) => ({
          ...(old as OutSourceConfig),
          [firstLevelProp]: value,
        }));

      setOutsource((old) => ({
        ...(old as OutSourceConfig),
        [firstLevelProp]: {
          ...(old?.[firstLevelProp as OutsourceKey] as OutsourceCost),
          [secondLevelProp]: value,
        },
      }));
    },
    [setOutsource],
  );

  return (
    <Column>
      <OutsourceProviderSelect value={outsource?.provider || ''} onChange={handleProviderChange} />

      <div className="grid grid-cols-2 gap-4 phone:grid-cols-1">
        <MonetaryInput
          onChange={handleChange}
          currency={outsource?.providerCost?.currency}
          currencyDisabled
          value={outsource?.providerCost?.value || ''}
          name="providerCost#value"
          required
          label={t('provide-cost')}
        />

        <Column gap="2">
          <MonetaryInput
            label={t('cost-to-client')}
            currency={outsource?.clientCost?.currency || ''}
            currencyDisabled
            value={outsource?.clientCost?.value}
            name="clientCost#value"
            disabled
          />

          <span className="text-xs font-medium italic text-muted-foreground">
            {t('this-is-the-entire-service-cost')}
          </span>
        </Column>
      </div>

      <LabeledTextarea
        label={t('add-a-comment')}
        name="justification"
        value={outsource?.justification || ''}
        onChange={handleChange}
      />
    </Column>
  );
}
